import { IconHeart, IconBookmark, IconCalendar, IconMapPin, IconEye, IconMusic, IconBriefcase, IconShare2, IconCalendarEvent, IconUsers, IconClock, IconInfoCircle, IconBuildingCommunity, IconArrowNarrowRight, IconMapSearch, IconAlertCircle, IconChevronRight } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Event } from '../types/event';
import { eventService, staffService } from '../services/api';
import styles from './EventDetails.module.css';
import { ROUTES } from '../constants/routes';
import { StaffRequest } from '../types/staff';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export function EventDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewCount, setViewCount] = useState<number>(0);
  const [staffRequests, setStaffRequests] = useState<StaffRequest[]>([]);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [similarEvents, setSimilarEvents] = useState<Event[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEvent();
    
    // Add event listener to handle clicks outside the share menu
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShareOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [id]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate eventId before making API call
      if (!id || isNaN(Number(id))) {
        setError('Identifiant d\'événement invalide');
        setLoading(false);
        return;
      }

      const data = await eventService.getById(Number(id));
      
      if (data) {
        setEvent(data);
        // Record view after successful fetch
        try {
          // Check if we've already viewed this event recently using localStorage
          const viewedEventsJson = localStorage.getItem('viewedEvents') || '{}';
          const viewedEvents = JSON.parse(viewedEventsJson);
          const eventKey = `event_${id}`;
          const lastViewTime = viewedEvents[eventKey];
          const currentTime = Date.now();
          
          // If we haven't viewed it in the last 30 minutes, record a view
          if (!lastViewTime || (currentTime - lastViewTime > 30 * 60 * 1000)) {
            console.log(`Recording view for event ${id} (details page)`);
            // Record the view time
            viewedEvents[eventKey] = currentTime;
            localStorage.setItem('viewedEvents', JSON.stringify(viewedEvents));
            
            try {
              // Try to use the API endpoint if it exists
              const response = await eventService.recordView(Number(id));
              // Update the local view count state with the returned count from the API
              setViewCount(response.viewCount || (data._count?.views || 0) + 1);
              
              // Also update the event object to reflect the new view count
              if (response.viewCount) {
                setEvent(prevEvent => {
                  if (!prevEvent) return data;
                  return {
                    ...prevEvent,
                    _count: {
                      ...(prevEvent._count || {}),
                      views: response.viewCount
                    }
                  };
                });
              }
            } catch (apiError) {
              console.error('API endpoint for recording views not available:', apiError);
              
              // Fallback: Increment the view count locally
              const currentViewCount = data._count?.views || 0;
              const incrementedCount = currentViewCount + 1;
              setViewCount(incrementedCount);
              
              // Update the event object with the incremented count (client-side only)
              setEvent(prevEvent => {
                if (!prevEvent) return data;
                return {
                  ...prevEvent,
                  _count: {
                    ...(prevEvent._count || {}),
                    views: incrementedCount
                  }
                };
              });
            }
          } else {
            console.log(`Skipping duplicate view for event ${id}, last viewed ${(currentTime - lastViewTime) / 1000}s ago`);
            // Just use the existing count
            setViewCount(data._count?.views || 0);
          }
        } catch (viewError) {
          console.error('Erreur lors de l\'enregistrement de la vue:', viewError);
          // Non-critical error, don't show to user
          // Still use the view count from the event data as fallback
          setViewCount(data._count?.views || 0);
        }
        
        // Fetch similar events - dans une implémentation réelle, vous devriez 
        // implémenter getSimilarEvents dans le service ou utiliser une alternative
        try {
          // Simuler des événements similaires car getSimilarEvents n'existe pas encore
          const eventsResponse = await eventService.getAll({
            category: data.eventCategory?.id,
            upcoming: true
          });
          
          // Filtrer pour exclure l'événement actuel et limiter à 3 événements
          const filteredEvents = eventsResponse.filter((e: Event) => e.id !== data.id).slice(0, 3);
          setSimilarEvents(filteredEvents || []);
        } catch (error) {
          console.error('Erreur lors de la récupération des événements similaires:', error);
          // Non-critical error
        }
      } else {
        setError('Événement non trouvé');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de l\'événement:', err);
      setError('Erreur lors de la récupération de l\'événement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Vérifier si l'utilisateur est l'organisateur de l'événement
    if (event && event.organization) {
      // Utiliser l'opérateur d'accès sécurisé avec une valeur par défaut
      const isUserOrganizer = event.organization?.isUserMember ?? false;
      setIsOrganizer(isUserOrganizer);
    }
    
    // Récupérer les demandes de staff pour cet événement
    if (event) {
      const loadStaffRequests = async () => {
        try {
          const staffRequestsData = await staffService.getStaffRequests(Number(id));
          setStaffRequests(staffRequestsData);
        } catch (err) {
          console.error("Erreur lors du chargement des demandes de staff:", err);
        }
      };
      
      loadStaffRequests();
    }
    
    // Initialize map if the event has coordinates
    if (event?.coordinates && mapRef.current && showMap) {
      initializeMap();
    }
  }, [event, id, showMap]);
  
  const initializeMap = () => {
    if (!event?.coordinates || !mapRef.current) return;
    
    // This is a placeholder for actual map implementation
    // In a real implementation, you would use a mapping library like Leaflet or Google Maps
    const mapDiv = mapRef.current;
    mapDiv.innerHTML = `
      <div class="${styles.mapPlaceholder}">
        <iframe 
          width="100%" 
          height="100%" 
          frameborder="0" 
          style="border:0" 
          src="https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${event.coordinates.lat},${event.coordinates.lon || event.coordinates.lng}" 
          allowfullscreen>
        </iframe>
      </div>
    `;
  };

  const handleInterestClick = async () => {
    if (!event) return;
    try {
      await eventService.toggleInterest(event.id);
      await fetchEvent();
    } catch (err) {
      console.error('Error toggling interest:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la mise à jour de l\'intérêt');
    }
  };

  const handleSaveClick = async () => {
    if (!event) return;
    try {
      await eventService.toggleSave(event.id);
      await fetchEvent();
    } catch (err) {
      console.error('Error toggling save:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la sauvegarde');
    }
  };

  const handleCopyLink = () => {
    if (!event) return;
    const eventUrl = window.location.href;
    navigator.clipboard.writeText(eventUrl)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(err => {
        console.error('Error copying to clipboard:', err);
      });
  };
  
  const handleShareClick = () => {
    setShareOpen(!shareOpen);
  };
  
  const shareOnSocialMedia = (platform: string) => {
    if (!event) return;
    
    const eventUrl = window.location.href;
    const text = `Découvrez cet événement : ${event.title}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(eventUrl)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${eventUrl}`)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
    
    setShareOpen(false);
  };
  
  const formatEventDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'EEEE d MMMM yyyy à HH:mm', { locale: fr });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };
  
  const formatTimeFromNow = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { locale: fr, addSuffix: true });
    } catch (e) {
      console.error('Error formatting time from now:', e);
      return '';
    }
  };
  
  const toggleMap = () => {
    setShowMap(!showMap);
  };
  
  const getDirectionsUrl = () => {
    if (!event?.coordinates) return '';
    return `https://www.google.com/maps/dir/?api=1&destination=${event.coordinates.lat},${event.coordinates.lon || event.coordinates.lng}`;
  };

  if (loading) {
    return <div className={styles.loading}>
      <div className={styles.loadingSpinner}></div>
      <p>Chargement de l'événement...</p>
    </div>;
  }

  if (error) {
    return (
      <div className={styles.error}>
        <IconAlertCircle size={48} />
        <h2>Erreur</h2>
        <p>{error}</p>
        <button onClick={() => navigate(ROUTES.HOME)}>
          Retourner à l'accueil
        </button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className={styles.error}>
        <IconAlertCircle size={48} />
        <h2>Événement introuvable</h2>
        <p>L'événement demandé n'existe pas ou a été supprimé.</p>
        <button onClick={() => navigate(ROUTES.HOME)}>
          Retourner à l'accueil
        </button>
      </div>
    );
  }

  // Pour les champs qui n'existent pas encore dans l'interface Event
  const eventStartDate = event.dateTime;
  const eventEndDate = event.dateTime; // Dans cette implémentation, on utilise dateTime comme fallback
  const distanceLabel = event.distance ? `À ${event.distance.toFixed(1)} km de vous` : '';

  return (
    <div className={styles.eventDetailsContainer}>
      <div className={styles.eventDetails}>
        <div className={styles.eventHeader}>
          {event.imageUrl ? (
            <img src={event.imageUrl} alt={event.title} className={styles.eventImage} />
          ) : (
            <div className={styles.eventPlaceholderImage}>
              <IconCalendarEvent size={64} />
            </div>
          )}
          <div className={styles.eventHeaderContent}>
            {event.eventCategory && (
              <span 
                className={styles.categoryBadge}
                style={{ backgroundColor: event.eventCategory.color || '#0066cc' }}
              >
                {event.eventCategory.name}
              </span>
            )}
            <h1 className={styles.eventTitle}>{event.title}</h1>
            <div className={styles.eventMetadata}>
              <div className={styles.eventMetadataItem}>
                <IconCalendar size={16} />
                <span>{formatEventDate(event.dateTime)}</span>
              </div>
              <div className={styles.eventMetadataItem}>
                <IconMapPin size={16} />
                <span>{event.location}</span>
              </div>
              {event.coordinates && event.distance !== undefined && (
                <div className={styles.eventDistanceBadge}>
                  <span>
                    {distanceLabel || 'Voir sur la carte'}
                  </span>
                </div>
              )}
            </div>
            
            <div className={styles.quickActions}>
              <button 
                className={`${styles.actionButton} ${event.isInterested ? styles.active : ''}`}
                onClick={handleInterestClick}
              >
                <IconHeart size={20} />
                <span>Ça m'intéresse</span>
              </button>

              <button 
                className={`${styles.actionButton} ${event.isSaved ? styles.active : ''}`}
                onClick={handleSaveClick}
              >
                <IconBookmark size={20} />
                <span>Sauvegarder</span>
              </button>
              
              <div className={styles.shareContainer}>
                <button 
                  className={styles.actionButton}
                  onClick={handleShareClick}
                >
                  <IconShare2 size={20} />
                  <span>Partager</span>
                </button>
                
                {shareOpen && (
                  <div className={styles.shareMenu} ref={shareMenuRef}>
                    <button 
                      className={styles.shareOption} 
                      onClick={() => shareOnSocialMedia('facebook')}
                    >
                      Facebook
                    </button>
                    <button 
                      className={styles.shareOption} 
                      onClick={() => shareOnSocialMedia('twitter')}
                    >
                      Twitter
                    </button>
                    <button 
                      className={styles.shareOption} 
                      onClick={() => shareOnSocialMedia('linkedin')}
                    >
                      LinkedIn
                    </button>
                    <button 
                      className={styles.shareOption} 
                      onClick={() => shareOnSocialMedia('whatsapp')}
                    >
                      WhatsApp
                    </button>
                    <button 
                      className={styles.shareOption} 
                      onClick={handleCopyLink}
                    >
                      {linkCopied ? "Lien copié!" : "Copier le lien"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.eventContent}>
          <div className={styles.eventMainContent}>
            <div className={styles.eventSection}>
              <h2 className={styles.eventSectionTitle}>
                <IconInfoCircle size={18} />
                <span>À propos de cet événement</span>
              </h2>
              <div className={styles.eventInfoCards}>
                <div className={styles.infoCard}>
                  <div className={styles.infoCardIcon}>
                    <IconCalendarEvent size={24} />
                  </div>
                  <div className={styles.infoCardContent}>
                    <h3>Date et heure</h3>
                    <p>{formatEventDate(eventStartDate)}</p>
                    {eventEndDate && eventEndDate !== eventStartDate && (
                      <p className={styles.secondaryInfo}>
                        Jusqu'au {formatEventDate(eventEndDate)}
                      </p>
                    )}
                    <div className={styles.eventTiming}>
                      <IconClock size={14} />
                      <span>{formatTimeFromNow(eventStartDate)}</span>
                    </div>
                  </div>
                </div>
                
                <div className={styles.infoCard}>
                  <div className={styles.infoCardIcon}>
                    <IconMapPin size={24} />
                  </div>
                  <div className={styles.infoCardContent}>
                    <h3>Lieu</h3>
                    <p>{event.location}</p>
                    {event.coordinates && (
                      <>
                        <button 
                          className={styles.mapToggle} 
                          onClick={toggleMap}
                        >
                          <IconMapSearch size={14} />
                          <span>{showMap ? 'Masquer la carte' : 'Afficher la carte'}</span>
                        </button>
                        {distanceLabel && (
                          <div className={styles.distanceInfo}>
                            {distanceLabel}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {event.organization && (
                  <div className={styles.infoCard}>
                    <div className={styles.infoCardIcon}>
                      <IconBuildingCommunity size={24} />
                    </div>
                    <div className={styles.infoCardContent}>
                      <h3>Organisateur</h3>
                      <p>{event.organization.name}</p>
                      <Link 
                        to={ROUTES.ORGANIZATION.replace(':id', event.organization.id.toString())} 
                        className={styles.orgLink}
                      >
                        <span>Voir le profil</span>
                        <IconChevronRight size={14} />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              {event.coordinates && showMap && (
                <div className={styles.mapContainer} ref={mapRef}>
                  <div className={styles.mapPlaceholder}>
                    <p>Chargement de la carte...</p>
                  </div>
                  {event.coordinates && (
                    <a 
                      href={getDirectionsUrl()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.directionsButton}
                    >
                      <IconArrowNarrowRight size={16} />
                      <span>Itinéraire</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className={styles.eventSection}>
              <h2 className={styles.eventSectionTitle}>
                <IconInfoCircle size={18} />
                <span>Description</span>
              </h2>
              <div className={styles.eventDescription}>
                {event.description || "Aucune description disponible pour cet événement."}
              </div>
            </div>

            {staffRequests.length > 0 && (
              <div className={styles.eventSection}>
                <h2 className={styles.eventSectionTitle}>
                  <IconBriefcase size={18} />
                  <span>Staff recherché</span>
                </h2>
                <div className={styles.staffRequestsList}>
                  {staffRequests.map(request => (
                    <div key={request.id} className={styles.staffRequestItem}>
                      <IconMusic size={16} />
                      <span>{request.staffType}</span>
                    </div>
                  ))}
                  <button
                    className={styles.viewStaffButton}
                    onClick={() => navigate(ROUTES.STAFF_REQUESTS.replace(':id', id || ''))}
                  >
                    Voir les détails et postuler
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className={styles.eventSidebar}>
            <div className={styles.sidebarCard}>
              <h3>Participations</h3>
              <div className={styles.eventStats}>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <IconHeart size={20} />
                  </div>
                  <div className={styles.statInfo}>
                    <div className={styles.statValue}>{event._count?.interests || 0}</div>
                    <div className={styles.statLabel}>Intéressés</div>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <IconEye size={20} />
                  </div>
                  <div className={styles.statInfo}>
                    <div className={styles.statValue}>{viewCount || event._count?.views || 0}</div>
                    <div className={styles.statLabel}>Vues</div>
                  </div>
                </div>
                {event.maxParticipants && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <IconUsers size={20} />
                    </div>
                    <div className={styles.statInfo}>
                      <div className={styles.statValue}>{event.maxParticipants}</div>
                      <div className={styles.statLabel}>Places</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {isOrganizer && (
              <div className={styles.sidebarCard}>
                <h3>Gestion de l'événement</h3>
                <button 
                  className={styles.adminButton}
                  onClick={() => navigate(ROUTES.STAFF_REQUESTS.replace(':id', id || ''))}
                >
                  <IconBriefcase size={16} />
                  <span>Gérer le staff</span>
                </button>
                <button 
                  className={styles.adminButton}
                  onClick={() => navigate(ROUTES.EVENT_EDIT.replace(':id', id || ''))}
                >
                  <IconCalendarEvent size={16} />
                  <span>Modifier l'événement</span>
                </button>
              </div>
            )}
            
            {similarEvents.length > 0 && (
              <div className={styles.sidebarCard}>
                <h3>Événements similaires</h3>
                <div className={styles.similarEventsList}>
                  {similarEvents.slice(0, 3).map(similarEvent => (
                    <Link 
                      key={similarEvent.id} 
                      to={`/events/${similarEvent.id}`}
                      className={styles.similarEventItem}
                    >
                      <div className={styles.similarEventImage}>
                        {similarEvent.imageUrl ? (
                          <img src={similarEvent.imageUrl} alt={similarEvent.title} />
                        ) : (
                          <div className={styles.similarEventPlaceholder}>
                            <IconCalendarEvent size={16} />
                          </div>
                        )}
                      </div>
                      <div className={styles.similarEventInfo}>
                        <h4>{similarEvent.title}</h4>
                        <div className={styles.similarEventMeta}>
                          <IconCalendar size={12} />
                          <span>{format(new Date(similarEvent.dateTime), 'dd MMM yyyy', { locale: fr })}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
